import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';

const SaveSearchModal = ({ open, onClose, filters, polygon }) => {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  
  const texts = {
    pt: {
      title: 'Salvar Busca',
      description: 'Salve esta busca para acessá-la rapidamente mais tarde',
      nameLabel: 'Nome da Busca',
      namePlaceholder: 'Ex: Casas perto da Disney',
      enableAlerts: 'Ativar Alertas',
      alertsDescription: 'Receba notificações quando novos imóveis corresponderem a esta busca',
      frequency: 'Frequência do Alerta',
      instant: 'Instantâneo',
      daily: 'Diário',
      weekly: 'Semanal',
      cancel: 'Cancelar',
      save: 'Salvar',
      saving: 'Salvando...',
      nameRequired: 'Por favor, dê um nome para sua busca',
      success: 'Busca salva com sucesso!',
      error: 'Erro ao salvar busca'
    },
    en: {
      title: 'Save Search',
      description: 'Save this search to access it quickly later',
      nameLabel: 'Search Name',
      namePlaceholder: 'Ex: Houses near Disney',
      enableAlerts: 'Enable Alerts',
      alertsDescription: 'Receive notifications when new properties match this search',
      frequency: 'Alert Frequency',
      instant: 'Instant',
      daily: 'Daily',
      weekly: 'Weekly',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      nameRequired: 'Please name your search',
      success: 'Search saved successfully!',
      error: 'Error saving search'
    }
  };
  const t = texts[language];
  
  const [name, setName] = useState('');
  const [enableAlert, setEnableAlert] = useState(false);
  const [alertFrequency, setAlertFrequency] = useState('daily');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t.nameRequired);
      return;
    }

    setSaving(true);
    try {
      // Build complete filters object - BUG 6-7 FIX
      const completeFilters = {
        ...filters,
        alerts_enabled: enableAlert,
        alert_frequency: alertFrequency
      };
      
      // Save the search with all filters
      const searchResponse = await api.post('/saved-searches', {
        name: name.trim(),
        filters: completeFilters,
        polygon: polygon,
        alerts_enabled: enableAlert,
        alert_frequency: alertFrequency
      });

      const searchId = searchResponse.data.id;

      // Create alert if enabled
      if (enableAlert) {
        await api.post('/alerts', {
          saved_search_id: searchId,
          alert_frequency: alertFrequency,
          enabled: true
        });
      }

      toast.success(t.success);
      setName('');
      setEnableAlert(false);
      onClose(true); // Pass true to indicate success
    } catch (error) {
      console.error('Error saving search:', error);
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !saving && onClose(open)}>
      <DialogContent data-testid="save-search-modal">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>
            {t.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="search-name">{t.nameLabel}</Label>
            <Input
              id="search-name"
              placeholder={t.namePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="search-name-input"
            />
          </div>

          <div className="flex items-center justify-between space-x-2 py-2">
            <div className="space-y-0.5">
              <Label>{t.enableAlerts}</Label>
              <p className="text-sm text-muted-foreground">
                {t.alertsDescription}
              </p>
            </div>
            <Switch
              checked={enableAlert}
              onCheckedChange={setEnableAlert}
              data-testid="enable-alert-switch"
            />
          </div>

          {enableAlert && (
            <div className="space-y-2">
              <Label htmlFor="alert-frequency">{t.frequency}</Label>
              <Select value={alertFrequency} onValueChange={setAlertFrequency}>
                <SelectTrigger id="alert-frequency" data-testid="alert-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">{t.instant}</SelectItem>
                  <SelectItem value="daily">{t.daily}</SelectItem>
                  <SelectItem value="weekly">{t.weekly}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-search-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t.saving}
              </>
            ) : (
              t.save
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveSearchModal;
